import React, { useState } from "react";
import PropTypes from "prop-types";
import Flatpickr from "react-flatpickr";
import { Alert, Button, Modal, Textarea } from "@opendate-app/setlist";

const FLATPICKR_FORMAT = "Y-m-d\\TH:i";
const DISPLAY_FORMAT = "M j, Y \\a\\t h:i K";
const RESCHEDULE_INPUT_ID = "gift-card-reschedule-at";

const pad = (value) => String(value).padStart(2, "0");

export const wallClock = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const GiftCardRescheduleModal = ({ giftCard, submitting, onClose, onSubmit }) => {
  const [deliverAt, setDeliverAt] = useState(giftCard.deliverAtLocal || "");
  const [note, setNote] = useState("");

  const changed = Boolean(deliverAt) && deliverAt !== giftCard.deliverAtLocal;
  const ready = !submitting && changed && Boolean(note.trim());

  const footer = (
    <div className="gift-card-modal-footer">
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" disabled={!ready} onClick={() => onSubmit({ deliverAt, note })}>
        Reschedule
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      icon={false}
      title="Reschedule delivery"
      description={`Gift email for •••• ${giftCard.last4}`}
      footer={footer}
    >
      <div className="gift-card-modal-body">
        <p className="gift-card-detail-muted">
          {giftCard.deliverAtLabel
            ? `Scheduled to be sent ${giftCard.deliverAtLabel} to ${giftCard.ownerEmail}.`
            : `Not sent yet. It goes to ${giftCard.ownerEmail}.`}
        </p>
        <label className="gift-card-reschedule-field" htmlFor={RESCHEDULE_INPUT_ID}>
          <span className="gift-card-reschedule-label">Send on</span>
          <Flatpickr
            className="gift-card-reschedule-input"
            onReady={(_dates, _value, picker) => { picker.altInput.id = RESCHEDULE_INPUT_ID; }}
            data-testid="gift-card-reschedule-at"
            options={{
              enableTime: true,
              dateFormat: FLATPICKR_FORMAT,
              altInput: true,
              altFormat: DISPLAY_FORMAT,
              altInputClass: "gift-card-reschedule-input",
              minDate: giftCard.rescheduleEarliest,
              maxDate: giftCard.rescheduleLatest,
            }}
            placeholder="Pick a date and time"
            value={deliverAt}
            onChange={([date]) => setDeliverAt(date ? wallClock(date) : "")}
          />
          <span className="gift-card-reschedule-hint">{`Time is in ${giftCard.deliveryTimeZone}.`}</span>
        </label>
        <Textarea
          label="Note (required)"
          rows={3}
          placeholder="Why is the delivery moving?"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <Alert
          color="brand"
          size="full-width"
          title="Nothing is sent now"
          description="The card is emailed once, at the new time. The recipient stays the same."
        />
      </div>
    </Modal>
  );
};

GiftCardRescheduleModal.propTypes = {
  giftCard: PropTypes.shape({
    last4: PropTypes.string.isRequired,
    ownerEmail: PropTypes.string,
    deliverAtLabel: PropTypes.string,
    deliverAtLocal: PropTypes.string,
    deliveryTimeZone: PropTypes.string.isRequired,
    rescheduleEarliest: PropTypes.string.isRequired,
    rescheduleLatest: PropTypes.string.isRequired,
  }).isRequired,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

GiftCardRescheduleModal.defaultProps = {
  submitting: false,
};

export default GiftCardRescheduleModal;
